// Conversation tab inside the JobModal. WYSIWYG editor backed by
// Tiptap (B / I / U / lists / link), HTML message bodies. Append-
// only — no edit, no replies; mistakes are corrected by posting
// again.
import { useCallback, useEffect, useRef, useState } from "react";
import type { JobMessageDto } from "../../../../../../shared/types/production-schedule";
import {
  deleteMessage,
  listMessages,
  postMessage,
} from "../hooks/useProductionApi";
import { ConversationEditor } from "./ConversationEditor";

interface Props {
  jobId: number;
  currentUserName: string;
}

export function ConversationTab({ jobId, currentUserName }: Props) {
  const [messages, setMessages] = useState<JobMessageDto[]>([]);
  const [error, setError] = useState<string | null>(null);
  const scrollerRef = useRef<HTMLDivElement>(null);

  const refresh = useCallback(async () => {
    try {
      const res = await listMessages(jobId);
      setMessages(res.messages);
      setError(null);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    }
  }, [jobId]);

  useEffect(() => { void refresh(); }, [refresh]);

  // Auto-scroll to the latest message whenever the list grows.
  useEffect(() => {
    const el = scrollerRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [messages.length]);

  const onPost = async (html: string, mentions: string[]) => {
    if (!html.trim()) return;
    await postMessage(jobId, html, mentions);
    await refresh();
  };

  const onRemove = async (messageId: number) => {
    if (!window.confirm("Delete this message?")) return;
    try {
      await deleteMessage(jobId, messageId);
      await refresh();
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    }
  };

  return (
    <div style={{ display: "flex", flexDirection: "column", height: "60vh", maxHeight: 600 }}>
      <div
        ref={scrollerRef}
        style={{
          flex: 1,
          overflowY: "auto",
          padding: "8px 4px",
          border: "1px solid var(--bsp-border)",
          borderRadius: 4,
          background: "var(--bsp-surface-muted)",
        }}
      >
        {messages.length === 0 && (
          <div style={{ textAlign: "center", padding: 24, fontSize: 12, color: "var(--bsp-fg-muted)" }}>
            No comments yet. Start the conversation — use @ to mention a colleague.
          </div>
        )}
        {messages.map((m) => (
          <MessageRow
            key={m.id}
            message={m}
            canDelete={m.authorName === currentUserName}
            onRemove={() => void onRemove(m.id)}
          />
        ))}
      </div>
      {error && (
        <div style={{ background: "var(--bsp-red-tint)", color: "var(--bsp-red-fg)", padding: 8, borderRadius: 4, margin: "8px 0", fontSize: 11 }}>
          {error}
        </div>
      )}
      <div style={{ marginTop: 8 }}>
        <ConversationEditor onSubmit={onPost} authorLabel={currentUserName} />
      </div>
    </div>
  );
}

function MessageRow({
  message,
  canDelete,
  onRemove,
}: {
  message: JobMessageDto;
  canDelete: boolean;
  onRemove: () => void;
}) {
  const body = message.body;
  // Legacy messages were plain text and rendered as pre-wrapped.
  // New messages from the Tiptap editor are HTML; switch render
  // mode based on the first non-whitespace char so old rows don't
  // accidentally render their literal angle brackets.
  const looksHtml = body.trimStart().startsWith("<");
  return (
    <div style={{
      padding: "8px 12px",
      borderBottom: "1px solid var(--bsp-border)",
      background: "var(--bsp-surface)",
    }}>
      <div style={{ display: "flex", alignItems: "baseline", justifyContent: "space-between", gap: 8 }}>
        <span style={{ fontSize: 11, fontWeight: 700, color: "var(--bsp-fg)" }}>
          {message.authorName}
        </span>
        <span style={{ fontSize: 10, color: "var(--bsp-fg-muted)", fontFamily: "var(--bsp-font-mono)" }}>
          {new Date(message.createdAt).toLocaleString("en-AU", { dateStyle: "short", timeStyle: "short" })}
          {canDelete && (
            <button
              onClick={onRemove}
              title="Delete"
              style={{ marginLeft: 8, background: "none", border: "none", color: "var(--bsp-fg-muted)", cursor: "pointer", fontSize: 12 }}
            >
              ×
            </button>
          )}
        </span>
      </div>
      {looksHtml ? (
        <div
          className="bsp-msg-body"
          style={{ fontSize: 12, color: "var(--bsp-fg)", marginTop: 3 }}
          // Body is HTML produced by the constrained Tiptap editor.
          // See ConversationEditor for the safety posture; we treat
          // exotic markup as accidental rather than malicious.
          // eslint-disable-next-line react/no-danger
          dangerouslySetInnerHTML={{ __html: body }}
        />
      ) : (
        <div style={{ fontSize: 12, color: "var(--bsp-fg)", marginTop: 3, whiteSpace: "pre-wrap" }}>
          {body}
        </div>
      )}
    </div>
  );
}
